import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import FEATURE_FLAG_NAMES from 'platform/utilities/feature-toggles/featureFlagNames';
import { useFeatureToggle } from 'platform/utilities/feature-toggles/useFeatureToggle';
import RoutedSavableApp from 'platform/forms/save-in-progress/RoutedSavableApp';
import environment from 'platform/utilities/environment';
import { useBrowserMonitoring } from 'platform/monitoring/Datadog';
import formConfig from '../config/form';
import submitTransform from '../config/submit-transformer';
import { WIP } from '../../shared/components/WIP';
import { workInProgressContent } from '../config/constants';
import { trackSubmissionSession } from '../utils/submissionAttemptTracking';

const EXCLUDED_DOMAINS = [
  'resource.digital.voice.va.gov',
  'browser-intake-ddog-gov.com',
  'google-analytics.com',
  'eauth.va.gov',
  'api.va.gov',
];

export const BROWSER_MONITORING_PROPS = {
  toggleName: 'form214138BrowserMonitoringEnabled',
  applicationId: '7fccf5de-fc36-44c4-9dd0-1687f521536e',
  clientToken: 'pub371bb36afe0e4c66507112e4a03011f5',
  site: 'ddog-gov.com',
  service: '21-4138',
  env: environment.vspEnvironment(),
  sessionSampleRate: 100,
  sessionReplaySampleRate:
    environment.vspEnvironment() === 'staging' ? 100 : 20,
  version: '1.0.0',
  trackUserInteractions: true,
  trackFrustrations: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  beforeSend: ({ action, type, resource }) => {
    // eslint-disable-next-line no-param-reassign
    if (action?.type === 'click') action.target.name = 'Form item';
    return !(
      type === 'resource' &&
      EXCLUDED_DOMAINS.some(domain => resource.url.includes(domain))
    );
  },
};

function App({
  location,
  children,
  showForm,
  isLoading,
  submissionStatus,
  formData,
  inProgressFormId,
}) {
  useBrowserMonitoring(BROWSER_MONITORING_PROPS);

  const { useToggleValue, useFormFeatureToggleSync, TOGGLE_NAMES } =
    useFeatureToggle();
  const isVisibilityEnabled = useToggleValue(
    TOGGLE_NAMES.decisionReviewForm214138VisibilityLogs,
  );
  const isValidationEnabled = useToggleValue(
    TOGGLE_NAMES.decisionReviewForm214138ValidationLogs,
  );

  // Sync the name-prefill-fix toggle into form data so the (pure, store-less)
  // submit transformer can gate the veteran name recovery on it.
  useFormFeatureToggleSync([
    {
      toggleName: 'decisionReviewForm214138NamePrefillFix',
      formKey: 'view:namePrefillFixEnabled',
    },
  ]);

  const prevStatusRef = useRef(null);
  const submissionSessionIdRef = useRef(null);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = submissionStatus;

    if (submissionStatus === prev) return;

    submissionSessionIdRef.current = trackSubmissionSession({
      isVisibilityEnabled,
      isValidationEnabled,
      status: submissionStatus,
      currentSessionId: submissionSessionIdRef.current,
      formData,
      getSubmittedData: () =>
        JSON.parse(submitTransform(formConfig, { data: formData })),
      inProgressFormId,
    });
  }, [
    submissionStatus,
    isVisibilityEnabled,
    isValidationEnabled,
    formData,
    inProgressFormId,
  ]);

  if (isLoading) {
    return (
      <va-loading-indicator
        message="Please wait while we load the application for you."
        class="vads-u-margin-y--4"
        set-focus
      />
    );
  }
  if (!showForm) {
    return <WIP content={workInProgressContent} />;
  }

  return (
    <RoutedSavableApp formConfig={formConfig} currentLocation={location}>
      {children}
    </RoutedSavableApp>
  );
}

App.propTypes = {
  children: PropTypes.element.isRequired,
  location: PropTypes.object.isRequired,
  formData: PropTypes.object,
  inProgressFormId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  showForm: PropTypes.bool,
  submissionStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

const mapStateToProps = state => ({
  isLoading: state?.featureToggles?.loading,
  showForm:
    toggleValues(state)[FEATURE_FLAG_NAMES.form214138] ||
    environment.isLocalhost(),
  submissionStatus: state?.form?.submission?.status,
  formData: state?.form?.data,
  inProgressFormId: state?.form?.loadedData?.metadata?.inProgressFormId,
});

export default connect(mapStateToProps)(App);
